import React, { useEffect, useState, useMemo } from "react";
import { Avatar, Box, Divider, Grid, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper, Typography, useMediaQuery, useTheme, FormControl,
    FormControlLabel, Radio, RadioGroup, DialogContent, DialogActions, DialogTitle } from '@mui/material';
import { getAllSites } from "../../../../selectors/AdminSelectors";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";
import { StyledAutocomplete, StyledTextField } from "../../../common/StyledTextField";
import { ApprovalOutlined, ArrowBackIos, ArrowForwardIos, AssignmentOutlined, Check, DescriptionOutlined, OpenInNew, Settings, StoreOutlined, StorefrontOutlined, Warning, WarningAmber } from "@mui/icons-material";
import { LoadingButton, TextButton, TooltipIconButton } from "../../../common/StyledButtons";
import Colors from "../../../../constants/Colors";
import StyledSubHeader from "../../../common/StyledSubHeader";
import StyledPage from "../../../common/StyledPage";
import StyledSection from "../../../common/StyledSection";
import { getSupportSummariesForSiteForAdmin, processSummaryManuallyForAdmin } from "../../../../actions/internal/AdminActions";
import { formatDateRangeFancy, formatDateTimeToFancyString, formatDateToFancyString } from "../../../../utilities/DateUtilities";
import StyledStatusIcon from "../../../common/StyledStatusIcon";
import StyledProgressCircle from "../../../common/StyledProgressCircle";
import ViewRequestModal from "../../modals/ViewRequestModal";
import StyledDialog from "../../../common/StyledDialog";
import { getUserId } from "../../../../selectors/UserSelectors";

const PAGE_LIMIT = 10;


const SUMMARY_TITLE_STYLE = { fontSize: '14px', marginBottom: '4px', marginTop: 0 };
const SUMMARY_VALUE_STYLE = { fontSize: '24px', margin: 0 };  

const AdminViewSummaryPage = ({ history, match }) => {
    const [allSites] = useState(getAllSites() || []);
    const [selectedSiteId, setSelectedSiteId] = useState();
    const [isLoadingSummary, setIsLoadingSummary] = useState(true);
    const [summaryData, setSummaryData] = useState();
    const [error, setError] = useState();
    const [requestInView, setRequestInView] = useState();
    const [showReviewDialog, setShowReviewDialog] = useState(false);
    const [isSubmittingReview, setIsSubmittingReview] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const SUMMARY_BOX_STYLE = {
        padding: '12px 0px',
        minHeight: isMobile ? 'auto' : '100px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    };

    const summaryId =
    match &&
    match.params &&
    match.params.summaryId
        ? match.params.summaryId
        : null;

    const siteId =
    match &&
    match.params &&
    match.params.siteId
        ? match.params.siteId
        : null;

    const site = allSites.find(s => s.site_id === siteId);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            if (!siteId) return;

            setIsLoadingSummary(true);
            const summaries = await getSupportSummariesForSiteForAdmin(siteId);
            const summary = summaries.find(s => s.summary_id === summaryId);

            setSummaryData(summary);
        } catch (ex) {
            setError('Could not load summary at this time');
        } finally {
            setIsLoadingSummary(false);
        }
    }

    const handleConfirmSummaryReview = async (selectedOption) => {
        try {
            if (!summaryData || !summaryData.summary_id || !siteId) return;
    
            setError();
            setIsSubmittingReview(true);

            const options = {
                do_not_bill: selectedOption === 'do_not_bill',
                do_not_notify: selectedOption === 'do_not_bill' || selectedOption === 'approve_quietly'
            }
    
            await processSummaryManuallyForAdmin(getUserId(), siteId, summaryData.summary_id, options);
            
            setIsSubmittingReview(false);
            setShowReviewDialog(false);
    
            await loadData();
        } catch (ex) {
            setError('Could not submit review at this time');
        } finally {
            setIsSubmittingReview(false);
        }
    };


    const renderSumamryDetails = () => {
        const { summary_time_period, summary_total_utilized, summary_support_plan, summary_reviewed_epoch } = summaryData;
        const totalHours = summary_total_utilized && summary_total_utilized.total_time_hours
            ? parseFloat(summary_total_utilized.total_time_hours)
            : 0.0;
  
        const rate = summary_support_plan.hourly_rate || 0;
        const included = summary_support_plan.included_hours || 0;
        const overage = Math.max(0, totalHours - included);
        const endPeriod = summary_time_period && summary_time_period.end || null;
        const reviewedDate = summary_reviewed_epoch;
        const amountDue = ((overage * rate) / 100).toFixed(2);
        const billDate = endPeriod
        ? new Date(new Date(summary_time_period.end).getTime() + (10 * 24 * 60 * 60 * 1000))
        : null;
    
        const rowStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid #e0e0e0',
        fontSize: '16px'
        };
    
        const labelStyle = { color: '#444' };
        const valueStyle = { fontWeight: 500 };

        const isReviewed =
        summaryData &&
            typeof summaryData.summary_reviewed_epoch !== 'undefined' &&
            summaryData.summary_reviewed_epoch !== null;

        const invoiceUrl =
            summaryData &&
            summaryData.summary_invoice_data &&
            summaryData.summary_invoice_data.hosted_invoice_url;

        let rightAdornment = null;

        const now = Date.now();
        const elevenDaysInMs = 11 * 24 * 60 * 60 * 1000;
        const isPendingAutoReview =
            !reviewedDate &&
            endPeriod &&
            now - new Date(endPeriod).getTime() <= elevenDaysInMs &&
            now >= new Date(endPeriod).getTime();

        if (isReviewed && invoiceUrl) {
            rightAdornment = (
                <TooltipIconButton
                    text="View Invoice"
                    icon={<OpenInNew sx={{ fontSize: '24px' }} />}
                    onClick={() => window.open(invoiceUrl, '_blank')}
                />
            );
        } else if (!isReviewed) {
            rightAdornment = (
                <TooltipIconButton
                    text="Review & Approve"
                    icon={<ApprovalOutlined sx={{ fontSize: '24px' }} />}
                    onClick={() => setShowReviewDialog(true)}
                />
            );
        }

        return (
        <StyledSection title="Summary Details" rightAdornment={rightAdornment}>
            <Box sx={{ padding: '0px 0px 0' }}>
                <h4 style={{ marginTop: 0, marginBottom: '5px', fontSize: '32px' }}>{site && site.site_details.site_name}</h4>
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                <Box sx={SUMMARY_BOX_STYLE}>
                    <h4 style={SUMMARY_TITLE_STYLE}>Cycle Period</h4>
                    <h3 style={SUMMARY_VALUE_STYLE}>{formatDateRangeFancy(summary_time_period.start, summary_time_period.end)}</h3>
                </Box>
                {isMobile && <Divider />}
                </Grid>
                <Grid item xs={12} sm={6}>
                <Box sx={SUMMARY_BOX_STYLE}>
                    <h4 style={SUMMARY_TITLE_STYLE}>Reviewed On</h4>
                    <h3 style={SUMMARY_VALUE_STYLE}>
                        {reviewedDate
                            ? formatDateTimeToFancyString(reviewedDate)
                            : isPendingAutoReview
                                ? 'Pending Auto Review'
                                : 'Needs Review'}
                    </h3>
                </Box>
                {isMobile && <Divider />}
                </Grid>
            </Grid>

            <Box sx={{ marginTop: 1, width: '100%' }}>
                <Paper
                variant="outlined"
                sx={{overflow: 'hidden' }}
                >
                <Box sx={{ padding: '16px 16px 0' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '5px', fontSize: '24px' }}>Billing Summary</h4>
                </Box>
                <Box sx={rowStyle}>
                    <span style={labelStyle}>Total Hours Utilized</span>
                    <span style={valueStyle}>{totalHours.toFixed(1)} hrs</span>
                </Box>
                <Box sx={rowStyle}>
                    <span style={labelStyle}>Hours Covered</span>
                    <span style={valueStyle}>{included.toFixed(1)} hrs</span>
                </Box>
        
                <Box sx={rowStyle}>
                    <span style={labelStyle}>Hours Billed</span>
                    <span style={valueStyle}>{overage.toFixed(2)} hrs</span>
                </Box>
        
                <Box sx={rowStyle}>
                    <span style={labelStyle}>Hourly Rate</span>
                    <span style={valueStyle}>${(rate/100).toFixed(2)}/hr</span>
                </Box>
        
                <Box sx={{
                    ...rowStyle,
                    backgroundColor: '#f8f8f8',
                    borderBottom: 'none',
                    fontSize: '18px'
                }}>
                    <span style={{ fontWeight: 'bold' }}>Total Amount Due</span>
                    <span style={{ fontWeight: 'bold' }}>${amountDue}</span>
                </Box>
                </Paper>
                <p style={{ textAlign: 'center' }} >Please note, the clients account is automatically charged 10 days after the cycle ends (on  {billDate ? billDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '-'}).</p>
            </Box>
                
        </StyledSection>
        );
        }

    const renderSummaryTimeEntries = (requests) => (
        <StyledSection title="Summary Time Entries">
            <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px', borderRadius: '8px', }}>
                <List style={{ flex: 1 }}>
                {requests.map((submission, index) => (
                    <ListItem
                        key={submission.submission_id}
                        sx={{
                            backgroundColor: '#fff',
                            marginBottom: '5px',
                            borderBottom:
                                index !== requests.length - 1
                                    ? '1px solid #e0e0e0'
                                    : 'none',
                        }}>
                        <ListItemAvatar><StyledStatusIcon status={submission.submission_status} /></ListItemAvatar>
                        <ListItemText
                        primary={submission.submission_fields && submission.submission_fields.title ? submission.submission_fields.title : 'Untitled Request'}
                        secondary={`Closed on ${formatDateToFancyString(submission.submission_closed_epoch)}`}
                        />
                        <ListItemSecondaryAction>
                        <TooltipIconButton
                            text="View Details"
                            icon={<AssignmentOutlined sx={{ color: Colors.primaryColor }} />}
                            onClick={() => setRequestInView({
                            ...submission,
                            submission_hours: submission.submission_time_entries,
                            })}
                        />
                        </ListItemSecondaryAction>
                    </ListItem>
                    ))}
                </List>
            </Paper>
        </StyledSection>
    )

    const renderContent = () => {

        const { summary_tracked_submissions } = summaryData;
        
        return (
            <>
            {renderSumamryDetails()}
            {renderSummaryTimeEntries(summary_tracked_submissions)}
            </>
        )
       
        }
    
    const renderLoadingSpinner = () => (
        <StyledProgressCircle />
    )

    const SummaryApprovalDialog = ({
        open,
        onClose,
        onConfirm,
        isLoading = false
    }) => {
        const [selectedOption, setSelectedOption] = useState('approve_notify');
    
        const handleChange = (event) => {
            setSelectedOption(event.target.value);
        };
    
        const handleConfirm = () => {
            onConfirm(selectedOption);
        };
    
        return (
            <StyledDialog
                fullWidth
                open={open}
                maxWidth="sm"
            >
            <DialogTitle>
                <Box>
                    <h4 style={{ margin: 0, padding: 0 }}>Approve Summary</h4>
                </Box>
            </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <p sx={{ fontSize: 16 }}>
                            Choose how you would like to approve this summary.
                        </p>
    
                        <FormControl component="fieldset" disabled={isSubmittingReview}>
                            <RadioGroup
                                value={selectedOption}
                                onChange={handleChange}
                            >
                                <FormControlLabel
                                    value="approve_notify"
                                    control={<Radio />}
                                    label="Approve & Notify"
                                />
                                <FormControlLabel
                                    value="approve_quietly"
                                    control={<Radio />}
                                    label="Approve Quietly (Client will be billed, if applicable)"
                                />
                                <FormControlLabel
                                    value="do_not_bill"
                                    control={<Radio />}
                                    label="Do Not Bill (Client will not be notified nor billed)"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </DialogContent>
    
                <DialogActions sx={{ padding: '16px' }}>
                    <TextButton onClick={onClose} >
                        Cancel
                    </TextButton>
                    <LoadingButton onClick={handleConfirm} isLoading={isLoading}>
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>
        );
    };

    return (
        <StyledPage>
            <StyledSubHeader title="View Summary" />
            {isLoadingSummary ? renderLoadingSpinner() : renderContent() }
            <ViewRequestModal
                open={!!requestInView}
                onClose={() => setRequestInView(null)}
                request={requestInView}
            />
            <SummaryApprovalDialog 
                open={showReviewDialog}
                onClose={() => setShowReviewDialog(false)}
                onConfirm={handleConfirmSummaryReview}
                isLoading={isSubmittingReview}
            />
        </StyledPage>
    );
};

export default withRouter(observer(AdminViewSummaryPage));